// טור (Tur) Calculation Logic
// Based on zodiac element relationships between inner and outer signs

export type ZodiacElement = 'אש' | 'אדמה' | 'אוויר' | 'מים';
export type TurValue = 'א' | 'ב' | 'ג' | 'ד' | 'ה' | 'ו' | '-';

export interface TurResult {
    value: TurValue;
    label: string;
}

// Map zodiac sign numbers (1-12) to their elements
const ZODIAC_ELEMENTS: Record<number, ZodiacElement> = {
    1: 'אש',    // טלה (Aries)
    2: 'אדמה',   // שור (Taurus)
    3: 'אוויר',     // תאומים (Gemini)
    4: 'מים',   // סרטן (Cancer)
    5: 'אש',    // אריה (Leo)
    6: 'אדמה',   // בתולה (Virgo)
    7: 'אוויר',     // מאזניים (Libra)
    8: 'מים',   // עקרב (Scorpio)
    9: 'אש',    // קשת (Sagittarius)
    10: 'אדמה',  // גדי (Capricorn)
    11: 'אוויר',    // דלי (Aquarius)
    12: 'מים',  // דגים (Pisces)
};

/**
 * Get the element of a zodiac sign
 */
export const getZodiacElement = (signNumber: number): ZodiacElement | null => {
    if (signNumber < 1 || signNumber > 12) {
        return null;
    }
    return ZODIAC_ELEMENTS[signNumber];
};

/**
 * Check if two elements form a specific combination (order-independent)
 */
const hasElementPair = (
    element1: ZodiacElement,
    element2: ZodiacElement,
    target1: ZodiacElement,
    target2: ZodiacElement
): boolean => {
    return (
        (element1 === target1 && element2 === target2) ||
        (element1 === target2 && element2 === target1)
    );
};

/**
 * Calculate טור based on inner and outer zodiac signs
 * 
 * @param innerSign - Zodiac sign number from birthdate (1-12)
 * @param outerSign - Zodiac sign number from gematria (1-12)
 * @returns TurResult with value (א-ו) and Hebrew label
 */
export const calculateTur = (innerSign: number, outerSign: number): TurResult => {
    // Handle invalid inputs
    if (innerSign < 1 || innerSign > 12 || outerSign < 1 || outerSign > 12) {
        return {
            value: '-',
            label: '?'
        };
    }

    const innerElement = getZodiacElement(innerSign);
    const outerElement = getZodiacElement(outerSign);

    if (!innerElement || !outerElement) {
        return {
            value: '-',
            label: '?'
        };
    }

    // טור א: Same zodiac sign (inner and outer identical)
    if (innerSign === outerSign) {
        return {
            value: 'א',
            label: 'א\' - מזל זהה - ' + innerElement
        };
    }

    // טור ב: Same element but different signs
    if (innerElement === outerElement) {
        return {
            value: 'ב',
            label: 'ב\' - יסוד זהה - ' + innerElement
        };
    }

    // טור ג: אדמה + Water (למטה) OR Air + אש (למעלה)
    if (hasElementPair(innerElement, outerElement, 'אדמה', 'מים')) {
        return {
            value: 'ג',
            label: 'ג\' למטה - ' + innerElement + ' ו' + outerElement
        };
    }
    if (hasElementPair(innerElement, outerElement, 'אוויר', 'אש')) {
        return {
            value: 'ג',
            label: 'ג\' למעלה - ' + innerElement + ' ו' + outerElement
        };
    }

    // טור ד: Air + Water (any order)
    if (hasElementPair(innerElement, outerElement, 'אוויר', 'מים')) {
        return {
            value: 'ד',
            label: 'ד\' - ' + innerElement + ' ו' + outerElement
        };
    }

    // טור ה: אדמה + Air (למטה) OR Water + אש (למעלה)
    if (hasElementPair(innerElement, outerElement, 'אדמה', 'אוויר')) {
        return {
            value: 'ה',
            label: 'ה\' למטה - ' + innerElement + ' ו' + outerElement
        };
    }
    if (hasElementPair(innerElement, outerElement, 'מים', 'אש')) {
        return {
            value: 'ה',
            label: 'ה\' למעלה - ' + innerElement + ' ו' + outerElement
        };
    }

    // טור ו: אדמה + אש (any order)
    if (hasElementPair(innerElement, outerElement, 'אדמה', 'אש')) {
        return {
            value: 'ו',
            label: 'ו\' - ' + innerElement + ' ו' + outerElement
        };
    }

    // Fallback (Should typically not be reached with valid 1-12 inputs)
    return {
        value: '-',
        label: 'לא מוגדר'
    };
};
